import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useLangPath } from "@/i18n/LangLayout";

const STORAGE_KEY = "cookie-consent-v1";

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();
  const langPath = useLangPath();

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        const id = window.setTimeout(() => setVisible(true), 600);
        return () => window.clearTimeout(id);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const handleChoice = (choice: "accepted" | "rejected") => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, date: new Date().toISOString() })
      );
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookies.title", "באנר עוגיות")}
      dir="rtl"
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-6 z-[60] mx-auto max-w-3xl rounded-xl border border-border bg-card/95 backdrop-blur-md shadow-2xl p-4 sm:p-5 animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="shrink-0 rounded-full bg-primary/10 p-2">
            <Cookie className="h-5 w-5 text-primary" aria-hidden="true" />
          </div>
          <div className="text-sm text-foreground leading-relaxed">
            <p className="font-semibold mb-1">
              {t("cookies.title", "אנו משתמשים בעוגיות")}
            </p>
            <p className="text-muted-foreground">
              {t(
                "cookies.description",
                "אנו משתמשים בעוגיות לשיפור חוויית הגלישה, ניתוח תנועה והתאמת תוכן. ניתן לקבל או לדחות את השימוש."
              )}{" "}
              <Link
                to={langPath("/terms")}
                className="text-primary underline underline-offset-2 hover:text-primary/80"
              >
                {t("cookies.policyLink", "מדיניות הפרטיות")}
              </Link>
            </p>
          </div>
        </div>
        <div className="flex flex-row-reverse gap-2 w-full sm:w-auto shrink-0">
          <Button
            size="sm"
            onClick={() => handleChoice("accepted")}
            className="flex-1 sm:flex-initial"
          >
            {t("cookies.accept", "אישור")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleChoice("rejected")}
            className="flex-1 sm:flex-initial"
          >
            {t("cookies.reject", "דחייה")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;